<script setup lang="tsx">
import type { Response, Audit, StartAuditRequest, PageRequest } from '@/models/api-generated'
import { BasicAudit, InternalAudit, PermInternalAudit, PermBasicAudit, type AuditType } from '@/models/enums-generated'
import http from '@/services/http'
import { auditTypeStr, formatDate, hasPermission } from '@/services/utils'
import auditStore from '@/stores/audit'
import toastStore from '@/stores/toast'
import columnStore from '@/stores/column'
import { computed, onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AuditSelect from './AuditSelect.vue'
import { useModalStore } from '@/stores/modal'
import methodEditor from '@/stores/method-editor'
import saveAs from 'file-saver'
import type { MethodFilter } from '@/models/filters-generated'
import { viewing } from '@/services/utils'

const hasBasicPerm = computed(() => hasPermission(PermBasicAudit))
const hasInternalPerm = computed(() => hasPermission(PermInternalAudit))

const start = ref<StartAuditRequest>({
	name: '',
	isPublic: true,
	auditType: BasicAudit,
})

const { t } = useI18n()
const audit = auditStore()
const toaster = toastStore()
const oldSelected = ref<Audit>()
const modal = useModalStore()
const isExportDisabled = ref(false)
const editor = methodEditor()

const activeAudit = (typ: AuditType) => {
	switch (typ) {
		case BasicAudit:
			return audit.activeBasic
		case InternalAudit:
			return audit.activeInternal
		default:
			return []
	}
}
const instanceStr = computed(() => {
	return document.body.querySelector('#view-select option:checked')?.innerHTML
})

onBeforeMount(async () => {
	await audit.fetch()
	if (hasBasicPerm.value && !hasInternalPerm.value) start.value.auditType = BasicAudit
	else if (!hasBasicPerm.value && hasInternalPerm.value) start.value.auditType = InternalAudit
})

async function startAudit(auditType: number, viewingValue: number) {
	const activeAudits = activeAudit(auditType)
	if (audit.active.length && activeAudits) {
		const activeAuditExists = activeAudits.some((x) => x.instance?.id === viewingValue || (!x.instance && viewingValue === 0))

		if (activeAuditExists && !confirm(t('audit.confirmNew'))) {
			return
		}
	}

	const { data } = await http.post<Response<any>>('api/audit/start', start.value)

	if (data.status === 'OK') {
		toaster.showSuccess(t('audit.created'))

		start.value.name = ''
		start.value.isPublic = true

		await audit.fetch()
	}
}

async function navigateToAudit(a: Audit, viewingValue: number) {
	const params = new URLSearchParams(document.location.search)

	params.delete('instance')

	if (viewingValue === 0 && a.instance) {
		params.set('instance', a.instance.id.toString())
	}

	params.set('auditName', a.name)
	document.location.href = 'app/methods?' + params.toString()
}

const onFinishAudit = async (id: number, name: string) => {
	const header = t('audit.finishDialog.title')
	const body = t('audit.finishDialog.body', { auditName: name })
	const footer = (
		<div class="d-flex gap-2">
			<button id="cancel-finish-audit-btn" type="button" class="btn btn-secondary" data-bs-dismiss="modal">
				{t('misc.cancel')}
			</button>
			<button id="complete-finish-audit-btn" type="button" class="btn btn-danger" onClick={() => finishAudit(id)}>
				{t('misc.complete')}
			</button>
		</div>
	)
	modal.show(header, body, footer)
}

async function finishAudit(id: number) {
	const { data } = await http.post<Response<any>>('api/audit/complete/' + id)

	if (data.status === 'OK') {
		toaster.showSuccess(t('audit.completed'))
		await audit.fetch()

		modal.isVisible = false
	}
}

async function emptyAudit(id: number) {
	const { data } = await http.post<Response<any>>('api/audit/empty/' + id)

	if (data.status === 'OK') {
		toaster.showSuccess(t('audit.emptied'))
		await audit.fetch()
		await editor.fetchCallback?.()

		modal.isVisible = false
	}
}

async function publishAudit(id: number) {
	const { data } = await http.post<Response<any>>('api/audit/publish/' + id)

	if (data.status === 'OK') {
		toaster.showSuccess(t('audit.published'))
		await audit.fetch()

		modal.isVisible = false
	}
}

async function exportAudit(a: Audit, saveAsExcel: boolean) {
	const root = columnStore().defineComponent('methods')

	const request: PageRequest<MethodFilter> = {
		page: 0,
		limit: 0,
		filter: root.filterValue() as MethodFilter,
	}

	isExportDisabled.value = true
	const { status, data } = await http.post<Blob>(`/api/audit/export/${a.id}?saveAsExcel=${saveAsExcel}`, request, {
		responseType: 'blob',
	})
	isExportDisabled.value = false

	if (status === 200 && data.size > 0) {
		saveAs(data, saveAsExcel ? 'audit.xlsx' : 'audit.csv')
	}
}

const columns = ['misc.name', 'misc.created', 'misc.public', 'audit.type', 'institution']
</script>

<template>
	<div class="table-responsive">
		<table v-if="audit.active.length" class="table">
			<thead>
				<tr>
					<th scope="col" :colspan="index === columns.length - 1 ? 2 : 1" v-for="(header, index) in columns" :key="header">
						{{ t(header) }}
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="a in audit.active" :key="a.id">
					<td scope="row" class="align-middle break-word">{{ a.name }}</td>
					<td scope="row" class="align-middle">{{ formatDate(a.createdAt) }}</td>
					<td scope="row" class="align-middle">{{ a.isPublic ? t('misc.yes') : t('misc.no') }}</td>
					<td scope="row" class="align-middle">{{ auditTypeStr(a.type) }}</td>
					<td scope="row" class="align-middle">{{ a.instance ? a.instance.name : t('misc.all') }}</td>
					<td scope="row" class="align-middle">
						<div class="dropdown">
							<button
								:id="'audit-dropdown-btn' + a.id"
								class="btn btn-secondary dropdown-toggle"
								type="button"
								data-bs-toggle="dropdown"
								aria-expanded="false"
							></button>
							<ul class="dropdown-menu">
								<li>
									<a class="dropdown-item" @click="navigateToAudit(a, viewing)">{{ t('misc.view') }}</a>
								</li>
								<li>
									<a class="dropdown-item" @click="exportAudit(a, true)">{{ t('exportExcel') }}</a>
								</li>
								<li v-if="(a.type === BasicAudit && hasBasicPerm) || (a.type === InternalAudit && hasInternalPerm)">
									<a class="dropdown-item" @click="emptyAudit(a.id)">{{ t('audit.empty') }}</a>
								</li>
								<li>
									<a class="dropdown-item" @click="exportAudit(a, false)">{{ t('exportCsv') }}</a>
								</li>
								<li v-if="(a.type === BasicAudit && hasBasicPerm) || (a.type === InternalAudit && hasInternalPerm && !a.isPublic)">
									<a class="dropdown-item" @click="publishAudit(a.id)">{{ t('misc.publish') }}</a>
								</li>
								<li v-if="(a.type === BasicAudit && hasBasicPerm) || (a.type === InternalAudit && hasInternalPerm)">
									<a class="dropdown-item text-danger" @click="onFinishAudit(a.id, a.name)">{{ t('misc.complete') }}</a>
								</li>
							</ul>
						</div>
					</td>
				</tr>
			</tbody>
			<br />
			<br />
			<br />
		</table>
	</div>

	<div class="card mb-3" v-if="hasBasicPerm || hasInternalPerm">
		<h5 class="card-header">{{ t('audit.startNew') }}</h5>

		<div class="card-body">
			<div class="form-group row mb-2">
				<label for="name" class="col-7 col-form-label">{{ t('misc.name') }}</label>
				<div class="col-5">
					<input type="text" class="form-control" id="name" v-model="start.name" maxlength="20" />
				</div>
			</div>

			<div class="form-group row mb-2">
				<label for="isPublic" class="col-7 col-form-label">{{ t('misc.public') }}</label>
				<div class="col-5 form-check form-switch align-self-center">
					<input id="isPublic" class="form-check-input" type="checkbox" v-model="start.isPublic" />
				</div>
			</div>
			<div class="form-group row mb-2" v-if="hasBasicPerm && hasInternalPerm">
				<label class="col-7 col-form-label">{{ t('audit.type') }}</label>
				<div class="col-5">
					<select v-model="start.auditType" class="form-select">
						<option :value="BasicAudit">{{ auditTypeStr(BasicAudit) }}</option>
						<option :value="InternalAudit">{{ auditTypeStr(InternalAudit) }}</option>
					</select>
				</div>
			</div>
			<div v-else class="form-group row mb-2">
				<label class="col-7 col-form-label">{{ t('audit.type') }}</label>
				<div class="col-5 text-end">
					{{ auditTypeStr(start.auditType) }}
				</div>
			</div>

			<div class="form-group row mb-2">
				<label class="col-7 col-form-label">{{ t('institution') }}</label>
				<div class="col-5 text-end">
					{{ instanceStr }}
				</div>
			</div>

			<div class="form-group row">
				<div class="col-7"></div>
				<div v-one-tooltip="!viewing ? $t('audit.cantAdd') : ''" class="col-5">
					<button
						id="create-new-audit-btn"
						class="btn btn-primary w-100"
						:disabled="!start.auditType || !viewing"
						@click="startAudit(start.auditType, viewing)"
					>
						{{ t('audit.new') }}
					</button>
				</div>
			</div>
		</div>
	</div>

	<div class="card mb-6" v-if="audit.old.length">
		<h5 class="card-header">{{ t('audit.finishedAudits') }}</h5>

		<div class="card-body">
			<div class="form-group row mb-2">
				<label for="old-audit-select" class="col-4 col-form-label">{{ t('misc.name') }}</label>
				<div class="col-8">
					<AuditSelect v-model="oldSelected" :options="audit.old"></AuditSelect>
				</div>
			</div>

			<div class="row mb-2">
				<div class="col-7"></div>
				<div class="col-5">
					<button
						id="navigate-to-audit-btn"
						class="btn btn-primary w-100"
						:disabled="!oldSelected"
						@click="navigateToAudit(oldSelected!, viewing)"
					>
						{{ t('misc.view') }}
					</button>
				</div>
			</div>

			<div class="row mb-2">
				<div class="col-7"></div>
				<div class="col-5">
					<button
						id="export-audit-btn"
						class="btn btn-primary w-100"
						:disabled="!oldSelected || isExportDisabled"
						@click="exportAudit(oldSelected!, false)"
					>
						{{ t('exportCsv') }}
					</button>
				</div>
			</div>

			<div class="row">
				<div class="col-7"></div>
				<div class="col-5">
					<button
						id="export-audit-btn"
						class="btn btn-primary w-100"
						:disabled="!oldSelected || isExportDisabled"
						@click="exportAudit(oldSelected!, true)"
					>
						{{ t('exportExcel') }}
					</button>
				</div>
			</div>
		</div>
	</div>

	<div class="card mb-6" v-if="!hasBasicPerm && !hasInternalPerm && !audit.active.length && !audit.old.length">
		<h5 class="card-header">{{ t('audit.noActiveAudits.header') }}</h5>

		<div class="card-body">{{ t('audit.noActiveAudits.body') }}</div>
	</div>

	<br />
	<br />
	<br />
</template>

<style lang="sass" scoped>
.break-word
	max-width: 15ch
	overflow-wrap: break-word

.dropdown-menu
	z-index: 2000

.table-responsive
	overflow: visible
</style>
